import { useEffect, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, EyeOff, Eye, AlertTriangle, X, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useAccessibleOverlay from "@/hooks/useAccessibleOverlay";

/**
 * Account / business danger zone.
 *
 * Three distinct visibility concepts:
 *   1. Deactivate Website: separate card (existing PublicWebsiteSettings).
 *   2. Hide business     : this card, "Hide" button.
 *   3. Delete account    : this card, "Delete account" button (90-day retention).
 */
export default function AccountDangerZone() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [modal, setModal] = useState(null); // "delete" | "hide" | "publish" | null
  const [working, setWorking] = useState(false);

  const isOwner = user?.role === "business_owner";

  async function loadStatus() {
    try {
      const { data } = await api.get("/account/status");
      setStatus(data);
    } catch { /* anonymous endpoint already gated by auth */ }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadStatus(); }, [user?.id]);

  async function doDelete() {
    setWorking(true);
    try {
      await api.post("/account/delete-request", { confirm: "DELETE" });
      toast.success("Your account has been scheduled for deletion.");
      setModal(null);
      logout();
      navigate("/", { replace: true });
    } catch (e) { toast.error(formatApiError(e)); } finally { setWorking(false); }
  }
  async function doHide() {
    setWorking(true);
    try {
      const { data } = await api.post("/account/hide-business", {});
      toast.success(data.message || "Business hidden.");
      setModal(null);
      loadStatus();
    } catch (e) { toast.error(formatApiError(e)); } finally { setWorking(false); }
  }
  async function doPublish() {
    setWorking(true);
    try {
      const { data } = await api.post("/account/publish-business", {});
      toast.success(data.message || "Business public again.");
      setModal(null);
      loadStatus();
    } catch (e) { toast.error(formatApiError(e)); } finally { setWorking(false); }
  }

  const businessHidden = status?.business_hidden;
  const websiteEnabled = status?.website_enabled;
  const isDemo = status?.is_demo;

  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-6" data-testid="account-danger-zone">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-700 grid place-items-center flex-shrink-0">
          <ShieldAlert className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-lg font-semibold text-rose-900">Danger zone</h2>
          <p className="text-xs text-rose-800/80 mt-0.5 leading-relaxed">
            Hide your business from public view, or delete your account. Hiding is reversible; deletion keeps a 3-month grace period before permanent purge.
          </p>

          {isOwner && (
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {businessHidden ? (
                <button onClick={() => setModal("publish")} disabled={working || isDemo}
                  data-testid="publish-business-btn"
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white border border-emerald-300 text-emerald-700 text-sm font-medium hover:bg-emerald-50 disabled:opacity-50">
                  <Eye className="w-4 h-4" /> Publish business
                </button>
              ) : (
                <button onClick={() => setModal("hide")} disabled={working || isDemo}
                  data-testid="hide-business-btn"
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
                  title={isDemo ? "The demo business cannot be hidden." : ""}>
                  <EyeOff className="w-4 h-4" /> Hide business
                </button>
              )}
              <button onClick={() => setModal("delete")} disabled={working || isDemo}
                data-testid="delete-account-btn"
                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-50">
                <Trash2 className="w-4 h-4" /> Delete account
              </button>
            </div>
          )}
          {!isOwner && (
            <div className="mt-4">
              <button onClick={() => setModal("delete")} disabled={working}
                data-testid="delete-account-btn"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-50">
                <Trash2 className="w-4 h-4" /> Delete account
              </button>
            </div>
          )}

          {isOwner && (
            <p className="mt-3 text-[11px] text-rose-800/70">
              <strong>Hide business</strong> removes you from <em>all</em> public surfaces (discovery, public profile, Enterprise mini-site, sitemap) but keeps your dashboard.
              This is different from <strong>deactivating your Enterprise website</strong>{websiteEnabled ? "" : " (currently off)"}, which only switches you back to the standard public profile.
            </p>
          )}
          {businessHidden && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-900 bg-amber-100 px-2 py-1 rounded-md" data-testid="business-hidden-banner">
              <EyeOff className="w-3.5 h-3.5" /> Business is currently hidden from public view.
            </div>
          )}
        </div>
      </div>

      {modal && (
        <Modal title={
          modal === "delete" ? (isOwner ? "Delete account and business?" : "Delete your account?")
          : modal === "hide" ? "Hide your business from public view?"
          : "Publish your business again?"
        } onClose={() => !working && setModal(null)}>
          {modal === "delete" && (
            <>
              <p className="text-sm text-slate-700 leading-relaxed">
                {isOwner
                  ? "Deleting your account will also deactivate your business in ConfirmPH. Your public listing, public profile, public vehicles, and Enterprise website will be hidden immediately. Your business data, bookings, vehicles, staff records, inspections, warnings, and related records will be retained for 3 months. After 3 months, the account and business data will be permanently deleted or anonymized where possible."
                  : "Your account will be deactivated first. We will keep required records for up to 3 months in case of booking disputes, security review, or legal/account recovery needs. After 3 months, your account data will be permanently deleted or anonymized where possible."}
              </p>
              {isOwner && (
                <p className="text-xs text-slate-600 mt-3 leading-relaxed bg-slate-50 border border-slate-200 rounded-md p-2.5">
                  You can also <strong>Hide your account and business</strong> from public view instead. This keeps your account and business data active privately, but removes your business from public discovery, public profile pages, public vehicle listings, and Enterprise website pages. You can publish it again later.
                </p>
              )}
              <div className="mt-4 flex flex-wrap gap-2 justify-end">
                <button onClick={() => setModal(null)} disabled={working}
                  className="px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100"
                  data-testid="delete-cancel-btn">Cancel</button>
                {isOwner && (
                  <button onClick={doHide} disabled={working}
                    className="px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-800 text-sm font-medium hover:bg-slate-50"
                    data-testid="delete-modal-hide-btn">Hide account</button>
                )}
                <button onClick={doDelete} disabled={working}
                  className="px-3 py-2 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700"
                  data-testid="delete-confirm-btn">{working ? "…" : "Delete account"}</button>
              </div>
            </>
          )}
          {modal === "hide" && (
            <>
              <p className="text-sm text-slate-700 leading-relaxed">
                Your business will be removed from <strong>all</strong> public surfaces: discovery, public profile, Enterprise mini-site, public vehicle cards, and the sitemap. Your dashboard, bookings, and data stay private and editable. Anyone visiting an old public link will see a generic "currently unavailable" message.
              </p>
              <p className="text-xs text-slate-500 mt-2">
                This is different from <strong>deactivating your Enterprise website</strong>, which only switches you back to the normal public profile.
              </p>
              <div className="mt-4 flex gap-2 justify-end">
                <button onClick={() => setModal(null)} disabled={working}
                  className="px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100">Cancel</button>
                <button onClick={doHide} disabled={working}
                  data-testid="hide-confirm-btn"
                  className="px-3 py-2 rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-900">{working ? "…" : "Hide business"}</button>
              </div>
            </>
          )}
          {modal === "publish" && (
            <>
              <p className="text-sm text-slate-700 leading-relaxed">
                Your business will return to public discovery and listings. If your Enterprise website is enabled, the multi-page mini-site will also become visible again.
              </p>
              <div className="mt-4 flex gap-2 justify-end">
                <button onClick={() => setModal(null)} disabled={working}
                  className="px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100">Cancel</button>
                <button onClick={doPublish} disabled={working}
                  data-testid="publish-confirm-btn"
                  className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700">{working ? "…" : "Publish business"}</button>
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  const titleId = useId();
  const { panelRef } = useAccessibleOverlay(true, onClose);
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 grid place-items-center p-4" onClick={onClose} data-testid="account-modal-backdrop">
      <div ref={panelRef} className="bg-white w-full max-w-md max-h-[calc(100vh-2rem)] overflow-y-auto rounded-2xl shadow-2xl border border-slate-200" onClick={(e) => e.stopPropagation()}
           role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} data-testid="account-modal">
        <div className="flex items-start justify-between p-4 border-b border-slate-100">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-rose-50 text-rose-700 grid place-items-center flex-shrink-0"><AlertTriangle className="w-4 h-4" /></div>
            <h2 id={titleId} className="font-display font-semibold text-slate-900 truncate">{title}</h2>
          </div>
          <button type="button" onClick={onClose} className="min-w-11 min-h-11 grid place-items-center rounded-md hover:bg-slate-100 text-slate-500" aria-label="Close dialog">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
